/**
 * DocumentVaultPage — Customer document management (Epic 4)
 */
import { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { Plus, Download, Trash2, Share2, ShieldCheck, FolderOpen, ArrowLeft } from 'lucide-react'
import DocumentUploadModal from '../components/DocumentUploadModal'
import { useAuth } from '../contexts/AuthContext'
import {
  listDocuments,
  getDownloadUrl,
  deleteDocument,
  shareDocument,
  DOCUMENT_TYPE_LABELS,
  type CustomerDocument,
  type DocumentType,
} from '../api/documentsApi'

const MAX_STORAGE = 100 * 1024 * 1024 // 100 MB

function formatSize(bytes: number) {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

function formatDate(iso: string | null) {
  if (!iso) return ''
  return new Date(iso).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' })
}

export default function DocumentVaultPage() {
  const { getAccessToken } = useAuth()
  const navigate = useNavigate()
  const [documents, setDocuments] = useState<CustomerDocument[]>([])
  const [storageUsed, setStorageUsed] = useState(0)
  const [loading, setLoading] = useState(true)
  const [showUpload, setShowUpload] = useState(false)
  const [actionId, setActionId] = useState<string | null>(null)

  const token = getAccessToken()

  const loadDocuments = useCallback(async () => {
    if (!token) return
    setLoading(true)
    try {
      const res = await listDocuments(token)
      setDocuments(res.documents)
      setStorageUsed(res.storage_used)
    } catch {
      // silent — empty state shown
    } finally {
      setLoading(false)
    }
  }, [token])

  useEffect(() => { loadDocuments() }, [loadDocuments])

  const handleDownload = async (doc: CustomerDocument) => {
    if (!token) return
    setActionId(doc.id)
    try {
      const { url } = await getDownloadUrl(token, doc.id)
      window.open(url, '_blank')
    } catch {
      alert('Failed to get download link. Please try again.')
    } finally {
      setActionId(null)
    }
  }

  const handleDelete = async (doc: CustomerDocument) => {
    if (!token || !confirm(`Delete "${doc.file_name}"? This cannot be undone.`)) return
    setActionId(doc.id)
    try {
      await deleteDocument(token, doc.id)
      setDocuments(prev => prev.filter(d => d.id !== doc.id))
      setStorageUsed(prev => prev - doc.file_size)
    } catch {
      alert('Failed to delete document.')
    } finally {
      setActionId(null)
    }
  }

  const handleShare = async (doc: CustomerDocument) => {
    if (!token) return
    setActionId(doc.id)
    try {
      const updated = await shareDocument(token, doc.id, !doc.shared_with_broker)
      setDocuments(prev => prev.map(d => d.id === updated.id ? updated : d))
    } catch {
      alert('Failed to update sharing.')
    } finally {
      setActionId(null)
    }
  }

  // Group by document type
  const grouped = (Object.keys(DOCUMENT_TYPE_LABELS) as DocumentType[]).reduce((acc, type) => {
    const docs = documents.filter(d => d.document_type === type)
    if (docs.length > 0) acc[type] = docs
    return acc
  }, {} as Partial<Record<DocumentType, CustomerDocument[]>>)

  const usagePct = Math.min(100, (storageUsed / MAX_STORAGE) * 100)

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <button onClick={() => navigate('/customer')} className="text-gray-500 hover:text-gray-700">
                <ArrowLeft size={20} />
              </button>
              <div>
                <h1 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                  <ShieldCheck size={22} className="text-blue-600" />
                  Document Vault
                </h1>
                <p className="text-sm text-gray-500">Securely upload and share documents with your broker</p>
              </div>
            </div>
            <button
              onClick={() => setShowUpload(true)}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700"
            >
              <Plus size={16} />
              Upload
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-6">
        {/* Storage usage bar */}
        <div className="bg-white rounded-lg border p-4 mb-6">
          <div className="flex items-center justify-between text-sm mb-2">
            <span className="text-gray-600">Storage Used</span>
            <span className="text-gray-900 font-medium">{formatSize(storageUsed)} / {formatSize(MAX_STORAGE)}</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div
              className={`h-2 rounded-full transition-all ${usagePct > 80 ? 'bg-red-500' : 'bg-blue-500'}`}
              style={{ width: `${usagePct}%` }}
            />
          </div>
        </div>

        {loading ? (
          <div className="text-center py-16 text-gray-500">Loading documents...</div>
        ) : documents.length === 0 ? (
          <div className="text-center py-16">
            <FolderOpen size={48} className="mx-auto text-gray-300 mb-4" />
            <h3 className="text-lg font-medium text-gray-700 mb-2">No documents yet</h3>
            <p className="text-gray-500 mb-6">Upload your identity, income, and property documents to share with your broker.</p>
            <button
              onClick={() => setShowUpload(true)}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium"
            >
              Upload Your First Document
            </button>
          </div>
        ) : (
          <div className="space-y-6">
            {(Object.entries(grouped) as [DocumentType, CustomerDocument[]][]).map(([type, docs]) => (
              <div key={type}>
                <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">
                  {DOCUMENT_TYPE_LABELS[type]}
                </h2>
                <div className="bg-white rounded-lg border divide-y">
                  {docs.map(doc => (
                    <div key={doc.id} className="flex items-center justify-between p-4">
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">{doc.file_name}</p>
                        <p className="text-xs text-gray-500">
                          {formatSize(doc.file_size)} &middot; {formatDate(doc.created_at)}
                        </p>
                      </div>
                      <div className="flex items-center gap-2 ml-4">
                        {/* Share toggle */}
                        <button
                          onClick={() => handleShare(doc)}
                          disabled={actionId === doc.id}
                          title={doc.shared_with_broker ? 'Shared with broker — click to unshare' : 'Click to share with broker'}
                          className={`p-2 rounded-lg text-sm transition-colors ${
                            doc.shared_with_broker
                              ? 'bg-green-50 text-green-600 hover:bg-green-100'
                              : 'bg-gray-50 text-gray-400 hover:bg-gray-100 hover:text-gray-600'
                          }`}
                        >
                          <Share2 size={16} />
                        </button>
                        {/* Download */}
                        <button
                          onClick={() => handleDownload(doc)}
                          disabled={actionId === doc.id}
                          className="p-2 rounded-lg bg-gray-50 text-gray-600 hover:bg-blue-50 hover:text-blue-600"
                          title="Download"
                        >
                          <Download size={16} />
                        </button>
                        {/* Delete */}
                        <button
                          onClick={() => handleDelete(doc)}
                          disabled={actionId === doc.id}
                          className="p-2 rounded-lg bg-gray-50 text-gray-400 hover:bg-red-50 hover:text-red-600"
                          title="Delete"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Upload modal */}
      {showUpload && token && (
        <DocumentUploadModal
          token={token}
          onClose={() => setShowUpload(false)}
          onUploaded={() => loadDocuments()}
        />
      )}
    </div>
  )
}
